/** Gateway IAM permissions and browser session token adapter. */

import { authFetchCredentials, clearSessionMarkers } from "@/config/authStorage";
import { getGatewayBaseUrl } from "@/config/gateway";
import { isKeycloakMode } from "@/config/environment";
import { KEYCLOAK_CONFIG, AUTH_REDIRECT_ON_401_FAILURE } from "@/lib/constants/config";

type TokenParsed = Record<string, unknown>;

interface TokenAdapterState {
  token?: string;
  refreshToken?: string;
  tokenParsed?: TokenParsed;
  authenticated?: boolean;
  onTokenExpired?: () => void;
}

const tokenState: TokenAdapterState = {};

type KeycloakClient = {
  token?: string;
  refreshToken?: string;
  tokenParsed?: TokenParsed;
  authenticated?: boolean;
  onTokenExpired?: () => void;
  updateToken(minValidity: number): Promise<boolean>;
};

let keycloakClientPromise: Promise<KeycloakClient | null> | null = null;

async function getKeycloakClient(): Promise<KeycloakClient | null> {
  if (!isKeycloakMode()) {
    return null;
  }
  if (!keycloakClientPromise) {
    keycloakClientPromise = import("keycloak-js").then(({ default: Keycloak }) => {
      const client = new Keycloak({
        url: KEYCLOAK_CONFIG.url,
        realm: KEYCLOAK_CONFIG.realm,
        clientId: KEYCLOAK_CONFIG.clientId,
      }) as KeycloakClient;
      if (tokenState.token !== undefined) {
        client.token = tokenState.token;
      }
      if (tokenState.refreshToken !== undefined) {
        client.refreshToken = tokenState.refreshToken;
      }
      if (tokenState.tokenParsed !== undefined) {
        client.tokenParsed = tokenState.tokenParsed;
      }
      if (tokenState.authenticated !== undefined) {
        client.authenticated = tokenState.authenticated;
      }
      if (tokenState.onTokenExpired !== undefined) {
        client.onTokenExpired = tokenState.onTokenExpired;
      }
      return client;
    });
  }
  return keycloakClientPromise;
}

const tokenAdapter: TokenAdapterState & { updateToken(minValidity: number): Promise<boolean> } = {
  get token() {
    return tokenState.token;
  },
  set token(value: string | undefined) {
    tokenState.token = value;
  },
  get refreshToken() {
    return tokenState.refreshToken;
  },
  set refreshToken(value: string | undefined) {
    tokenState.refreshToken = value;
  },
  get tokenParsed() {
    return tokenState.tokenParsed;
  },
  set tokenParsed(value: TokenParsed | undefined) {
    tokenState.tokenParsed = value;
  },
  get authenticated() {
    return tokenState.authenticated;
  },
  set authenticated(value: boolean | undefined) {
    tokenState.authenticated = value;
  },
  get onTokenExpired() {
    return tokenState.onTokenExpired;
  },
  set onTokenExpired(handler: (() => void) | undefined) {
    tokenState.onTokenExpired = handler;
  },
  async updateToken(minValidity: number) {
    const client = await getKeycloakClient();
    if (!client) {
      return false;
    }
    const refreshed = await client.updateToken(minValidity);
    tokenState.token = client.token;
    tokenState.refreshToken = client.refreshToken;
    tokenState.tokenParsed = client.tokenParsed;
    tokenState.authenticated = client.authenticated;
    return refreshed;
  },
};

export const SESSION_EXPIRED_EVENT = "intake:session-expired";

let sessionInvalidationInProgress = false;
/** Bumped when a fresh login wins over an in-flight 401 teardown so logout cannot clear new cookies. */
let sessionInvalidationGeneration = 0;
let refreshSessionPromise: Promise<boolean> | null = null;

/**
 * Decode JWT payload (base64url).
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return {};
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Access JWT past exp (60s skew). */
export function isAccessTokenExpired(token: string): boolean {
  return isAccessTokenExpiringWithin(token, 60);
}

/** Access JWT exp within skewSeconds; missing exp is expired. */
export function isAccessTokenExpiringWithin(token: string, skewSeconds: number): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload.exp;
  if (typeof exp !== "number") return true;
  if (!Number.isFinite(skewSeconds) || skewSeconds < 0) {
    throw new Error("skewSeconds must be a non-negative finite number");
  }
  return exp < Math.floor(Date.now() / 1000) + Math.floor(skewSeconds);
}

function isTokenExpired(token: string): boolean {
  return isAccessTokenExpired(token);
}

function buildLoginUrlWithReturnPath(): string {
  if (typeof window === "undefined") return "/login";
  const path = window.location.pathname + window.location.search;
  if ([path === "/login", path.startsWith("/login?")].includes(true)) return "/login";
  return `/login?returnUrl=${encodeURIComponent(path)}`;
}

async function logoutGatewaySession(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }
  try {
    await fetch(`${getGatewayBaseUrl()}/v1/auth/logout`, {
      method: "POST",
      credentials: authFetchCredentials(),
      headers: { Accept: "application/json" },
    });
  } catch {
    /* gateway unreachable; client session still cleared */
  }
}

export function isSessionInvalidationInProgress(): boolean {
  return sessionInvalidationInProgress;
}

/**
 * Call after a successful credential login (before session hydrate).
 * Cancels an in-flight 401 teardown so logout cannot wipe the new HttpOnly cookies.
 */
export function acknowledgeAuthenticatedSession(): void {
  sessionInvalidationInProgress = false;
  sessionInvalidationGeneration += 1;
}

function resolveSessionRedirectTarget(redirectTo?: string): string {
  return redirectTo ?? buildLoginUrlWithReturnPath();
}

function shouldRedirectAfterSessionInvalidation(options?: { redirect?: boolean }): boolean {
  return options?.redirect !== false && AUTH_REDIRECT_ON_401_FAILURE;
}

function redirectToLoginHard(redirectTo?: string): void {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (path === "/login" || path.startsWith("/login/")) return;
  window.location.replace(resolveSessionRedirectTarget(redirectTo));
}

/**
 * Clear session state, notify AuthContext, and hard-redirect to login.
 * Awaits gateway logout so HttpOnly cookies clear before navigation.
 */
export function invalidateSession(options?: { redirect?: boolean; redirectTo?: string }): void {
  const shouldRedirect = shouldRedirectAfterSessionInvalidation(options);
  const redirectTo = options?.redirectTo;

  if (sessionInvalidationInProgress) {
    if (shouldRedirect) redirectToLoginHard(redirectTo);
    return;
  }
  sessionInvalidationInProgress = true;
  const generation = sessionInvalidationGeneration;

  clearPolicySnapshotPermissions();
  clearStoredTokens();
  tokenState.token = undefined;
  tokenState.refreshToken = undefined;
  tokenState.tokenParsed = undefined;
  tokenState.authenticated = false;

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }

  void (async () => {
    // A newer login may have acknowledged a session while this teardown was queued.
    if (generation !== sessionInvalidationGeneration) {
      return;
    }
    await logoutGatewaySession();
    if (generation !== sessionInvalidationGeneration) {
      return;
    }
    if (shouldRedirect) redirectToLoginHard(redirectTo);
  })();
}

export async function refreshSessionTokens(): Promise<boolean> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = (async () => {
      if (await refreshTokensViaApi()) return true;
      if (!getToken()) return false;
      try {
        await tokenAdapter.updateToken(30);
        const token = getToken();
        return Boolean(token && !isTokenExpired(token));
      } catch {
        return false;
      }
    })().finally(() => {
      refreshSessionPromise = null;
    });
  }
  return refreshSessionPromise;
}

export async function ensureAccessTokenFresh(): Promise<boolean> {
  if (typeof window === "undefined") return true;

  const token = getToken();
  if (token && !isTokenExpired(token)) return true;
  if (!token) return true;

  const refreshed = await refreshSessionTokens();
  if (refreshed) return true;

  tokenState.token = undefined;
  tokenState.refreshToken = undefined;
  tokenState.tokenParsed = undefined;
  tokenState.authenticated = false;
  return true;
}

/**
 * Set token from external source for legacy in-memory token adapters.
 * Browser session persistence is owned by the gateway HttpOnly cookie.
 */
export function setTokenFromCallback(accessToken: string, refreshToken?: string): void {
  tokenAdapter.token = accessToken;
  if (refreshToken !== undefined) {
    tokenAdapter.refreshToken = refreshToken;
  }
  tokenAdapter.tokenParsed = decodeJwtPayload(accessToken);
  tokenAdapter.authenticated = true;
  tokenAdapter.onTokenExpired = () => {
    void tokenAdapter.updateToken(30).catch((err) => {
      console.error("Failed to refresh token:", err);
    });
  };
}

/** Clear stored tokens (call on logout). */
export function clearStoredTokens(): void {
  clearSessionMarkers();
}

/**
 * Refresh tokens via our API (no redirect to IdP).
 * Returns true if new tokens were set.
 */
export async function refreshTokensViaApi(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const res = await fetch(`${getGatewayBaseUrl()}/v1/auth/refresh`, {
      method: "POST",
      credentials: authFetchCredentials(),
      headers: { "Content-Type": "application/json" },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) clearStoredTokens();
      return false;
    }
    const responseData = data && typeof data === "object" && "data" in data ? (data as { data?: Record<string, unknown> }).data : data;
    if (responseData && typeof responseData === "object" && "access_token" in responseData) {
      const accessToken = String((responseData as { access_token?: unknown }).access_token ?? "");
      const refreshTokenValue = (responseData as { refresh_token?: unknown }).refresh_token;
      const refreshToken = refreshTokenValue === undefined ? undefined : String(refreshTokenValue);
      setTokenFromCallback(accessToken, refreshToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Restore token from gateway session cookie after page load.
 */
export async function restoreTokenFromStorage(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  return refreshTokensViaApi();
}

/**
 * Logout: revoke gateway session cookies, clear client state, hard-redirect to /login.
 */
export function logout(redirectUri?: string): void {
  invalidateSession({ redirectTo: redirectUri ?? "/login" });
}

/**
 * Get current access token.
 *
 * @returns Access token or null
 */
export function getToken(): string | null {
  return tokenAdapter.token ?? null;
}

/**
 * Check if user is authenticated.
 *
 * @returns True if authenticated
 */
export function isAuthenticated(): boolean {
  const token = tokenAdapter.token;
  return Boolean(tokenAdapter.authenticated && token && !isTokenExpired(token));
}

export const HUB_ROLE_PRIORITY = ["admin", "lead", "member", "viewer"] as const;
export type HubRole = (typeof HUB_ROLE_PRIORITY)[number];

export function normalizeRoleName(role: string | null | undefined): HubRole | null {
  const normalized = String(role ?? "").trim().toLowerCase();
  if (!normalized) return null;
  return HUB_ROLE_PRIORITY.includes(normalized as HubRole) ? (normalized as HubRole) : null;
}

/**
 * Get user roles from token.
 * Checks both realm_access (realm roles) and resource_access (client roles).
 *
 * @returns Array of role names
 */
export function getUserRoles(): string[] {
  if (!tokenAdapter.tokenParsed) {
    return [];
  }

  const roles = new Set<HubRole>();

  const realmAccess = tokenAdapter.tokenParsed.realm_access as { roles?: string[] } | undefined;
  if (realmAccess?.roles) {
    for (const role of realmAccess.roles) {
      const normalized = normalizeRoleName(role);
      if (normalized) roles.add(normalized);
    }
  }

  const resourceAccess = tokenAdapter.tokenParsed.resource_access as
    | Record<string, { roles?: string[] }>
    | undefined;
  if (resourceAccess) {
    Object.keys(resourceAccess).forEach((clientId) => {
      const clientAccess = resourceAccess[clientId];
      if (clientAccess?.roles) {
        for (const role of clientAccess.roles) {
          const normalized = normalizeRoleName(role);
          if (normalized) roles.add(normalized);
        }
      }
    });
  }

  return HUB_ROLE_PRIORITY.filter((role) => roles.has(role));
}

export function getPrimaryRole(): HubRole | null {
  const roleSet = new Set(getUserRoles());
  for (const role of HUB_ROLE_PRIORITY) {
    if (roleSet.has(role)) {
      return role;
    }
  }
  return null;
}

export type HubPermission = string;

export const HUB_PERMISSION_KEYS = {
  PROJECTS_READ: "work_ops.projects.read",
  PROJECTS_MANAGE: "work_ops.projects.manage",
  PROJECTS_ASSIGN_LEAD: "work_ops.projects.assign_lead",
  PROJECTS_DELETE: "work_ops.projects.delete",
  CONVERSATION_OPS_READ: "conversation_ops.read",
  CONVERSATION_OPS_MANAGE: "conversation_ops.manage",
  COMMERCIAL_PARTY_READ: "commercial_party.read",
  COMMERCIAL_PARTY_MANAGE: "commercial_party.manage",
  TASKS_MANAGE: "work_ops.tasks.manage",
  TASKS_EXECUTE: "work_ops.tasks.execute",
  TASKS_REVIEW: "work_ops.tasks.review",
  TASKS_DELETE: "work_ops.tasks.delete",
  DRAFT_READ: "draft.read",
  DRAFT_AMEND: "draft.amend",
  DRAFT_DELETE_HARD: "draft.delete_hard",
  DRAFT_PREPARE_BYPASS: "draft.prepare.bypass",
  PROMPT_TEMPLATES_ADMIN_WRITE: "governance.prompt_templates.admin.write",
  INTAKE_ITEMS_READ: "intake.items.read",
  INTAKE_ITEMS_WRITE: "intake.items.write",
  INTAKE_ITEMS_ROUTE: "intake.items.route",
  INTAKE_REVIEW_READ: "intake.review.read",
  INTAKE_REVIEW_MANAGE: "intake.review.manage",
  INTAKE_CHANNELS_READ: "intake.channels.read",
  INTAKE_CHANNELS_MANAGE: "intake.channels.manage",
  INTAKE_LEDGER_READ: "intake.ledger.read",
  INTAKE_LEDGER_EXPORT: "intake.ledger.export",
  RECORDS_READ: "records.read",
  RECORDS_DELETE_SOFT: "records.delete_soft",
  RECORDS_DELETE_HARD: "records.delete_hard",
  ADMIN_ACCESS: "admin.access",
  SETTINGS_OPERATIONAL_READ: "settings.operational.read",
} as const;

const policySnapshotPermissions = new Set<string>();
let policySnapshotHydrated = false;
let policySnapshotLoadFailed = false;

function normalizePermissionKey(permission: string): string {
  return String(permission ?? "").trim();
}

function splitGatewayPermission(permission: string): { service: string; action: string } | null {
  const normalized = normalizePermissionKey(permission);
  const chunks = normalized.split("__").map((chunk) => chunk.trim()).filter(Boolean);
  if (chunks.length < 3) return null;
  return { service: chunks[0], action: chunks[chunks.length - 1] };
}

export function canonicalizePermissionKey(permission: string): string {
  const normalized = normalizePermissionKey(permission);
  if (!normalized) return "";
  const gateway = splitGatewayPermission(normalized);
  if (gateway) return `${gateway.service}.${gateway.action}`;
  return normalized;
}

function hasSnapshotPermission(permission: HubPermission): boolean {
  const required = normalizePermissionKey(permission);
  if (!required) return false;
  return policySnapshotPermissions.has(required);
}

export function setPolicySnapshotPermissions(permissions: readonly string[]): void {
  policySnapshotPermissions.clear();
  policySnapshotHydrated = true;
  for (const permission of permissions) {
    const normalized = normalizePermissionKey(permission);
    if (normalized) {
      policySnapshotPermissions.add(normalized);
    }
  }
}

export function clearPolicySnapshotPermissions(): void {
  policySnapshotPermissions.clear();
  policySnapshotHydrated = false;
}

/** Gateway IAM hydrate failed — fail closed (no role-map cheat). */
export function markPolicySnapshotLoadFailed(failed: boolean): void {
  policySnapshotLoadFailed = failed;
}

export function isPolicySnapshotHydrated(): boolean {
  return policySnapshotHydrated;
}

export function hasPermission(permission: HubPermission, role: HubRole | null = getPrimaryRole()): boolean {
  void role;
  if (policySnapshotLoadFailed) return false;
  if (!policySnapshotHydrated) return false;
  return hasSnapshotPermission(permission);
}

export function hasAnyPermission(permissions: readonly HubPermission[], role: HubRole | null = getPrimaryRole()): boolean {
  return permissions.some((permission) => hasPermission(permission, role));
}

export type PermissionMatch = "any" | "all";
export type PermissionActionState = "pending" | "allowed" | "denied";

export interface PermissionSnapshotFlags {
  authenticated: boolean;
  policySnapshotReady: boolean;
  policySnapshotError: boolean;
}

/** Action and route gates share this state machine. Empty requirements stay allowed. */
export function evaluatePermissionActionState(
  permissions: readonly string[],
  match: PermissionMatch,
  flags: PermissionSnapshotFlags,
  granted: (permission: string) => boolean,
): PermissionActionState {
  if (permissions.length === 0) return "allowed";
  if (!flags.authenticated) return "denied";
  if (!flags.policySnapshotReady && !flags.policySnapshotError) return "pending";
  const allowed = match === "all" ? permissions.every((permission) => granted(permission)) : permissions.some((permission) => granted(permission));
  return allowed ? "allowed" : "denied";
}

export function permissionKeysLabel(permissions: readonly string[]): string {
  return permissions.map((permission) => permission.trim()).filter((permission) => permission.length > 0).join(", ");
}

/** Pending and denied block the handler. Allowed runs it. */
export function invokeWhenPermitted(state: PermissionActionState, action: () => void): void {
  if (state !== "allowed") return;
  action();
}

export function canExecuteTasks(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.TASKS_EXECUTE);
}

export function canReviewTasks(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.TASKS_REVIEW);
}

export function canManageTasks(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.TASKS_MANAGE);
}

export function canReadProjects(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.PROJECTS_READ);
}

export function canAssignProjectLead(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.PROJECTS_ASSIGN_LEAD);
}

export function canManageCustomers(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.COMMERCIAL_PARTY_MANAGE);
}

export function canPullCustomers(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.COMMERCIAL_PARTY_MANAGE);
}

export function canSyncCustomerLinks(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.COMMERCIAL_PARTY_MANAGE);
}

export function canDeleteCustomers(): boolean {
  return hasAnyPermission([
    HUB_PERMISSION_KEYS.COMMERCIAL_PARTY_MANAGE,
    HUB_PERMISSION_KEYS.PROJECTS_DELETE,
  ]);
}

export function canReadConversationOps(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.CONVERSATION_OPS_READ);
}

export function canManageConversationOps(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.CONVERSATION_OPS_MANAGE);
}

export function hasAdminAccess(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.ADMIN_ACCESS);
}

/** Cascade delete for Hub tasks is governed by IAM action permission. */
export function canDeleteTasks(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.TASKS_DELETE);
}

export function canAssignPendingTaskWithoutClaim(): boolean {
  return canManageTasks();
}

export function canViewTaskAdminPanels(): boolean {
  return hasAdminAccess();
}

/** Project delete is governed by IAM action permission. */
export function canDeleteProjects(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.PROJECTS_DELETE);
}

export function canDeleteDraftHard(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.DRAFT_DELETE_HARD);
}

export function canBypassDraftPrepare(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.DRAFT_PREPARE_BYPASS);
}

export function canDebugTriageTemplates(): boolean {
  return hasAnyPermission([
    HUB_PERMISSION_KEYS.PROMPT_TEMPLATES_ADMIN_WRITE,
    HUB_PERMISSION_KEYS.TASKS_MANAGE,
  ]);
}

/** Soft-delete (archive) intake records — governed by IAM `records.delete_soft`. */
export function canDeleteRecordsSoft(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.RECORDS_DELETE_SOFT);
}

/** Permanent record delete — governed by IAM `records.delete_hard`. */
export function canDeleteRecordsHard(): boolean {
  return hasPermission(HUB_PERMISSION_KEYS.RECORDS_DELETE_HARD);
}

/**
 * Get user info from token.
 *
 * @returns User info object
 */
function displayNameFromTokenParsed(tokenParsed: Record<string, unknown> | undefined): string | undefined {
  if (!tokenParsed) return undefined;
  const name = typeof tokenParsed.name === "string" ? tokenParsed.name.trim() : "";
  if (name) return name;
  const given = typeof tokenParsed.given_name === "string" ? tokenParsed.given_name.trim() : "";
  const family = typeof tokenParsed.family_name === "string" ? tokenParsed.family_name.trim() : "";
  const combined = [given, family].filter(Boolean).join(" ").trim();
  return combined.length > 0 ? combined : undefined;
}

export function getUserInfo(): {
  id: string | undefined;
  email: string | undefined;
  username: string | undefined;
  displayName: string | undefined;
  roles: string[];
} {
  const tokenParsed = tokenAdapter.tokenParsed as Record<string, unknown> | undefined;

  return {
    id: tokenParsed?.sub as string | undefined,
    email: tokenParsed?.email as string | undefined,
    username: tokenParsed?.preferred_username as string | undefined,
    displayName: displayNameFromTokenParsed(tokenParsed),
    roles: getUserRoles(),
  };
}
